/**
 * 设置页「我屏蔽的」（WIRE-01 A6，总账 N-05；文案 I 10-10 定稿）：放在「隐私说明」下面。原来只接了 POST /blocks，屏蔽了就解不开。
 * - 读 GET /blocks（只列本人设的，新的在前）；列表底下「再看更早的」把上一页的 next_cursor 原样作查询参数 cursor 带上取下一页。
 *   后端说游标看不懂（422，details.reason = bad_cursor）：回到第一页重新看，不提示技术原因。
 * - 每一项：名字照后端给的 display_name 原样（宠物是屏蔽那一刻的公开名字；旧范围那一项固定「以前屏蔽过的全部星球居民动态」，照样能解除），
 *   小字「10 月 9 日屏蔽」；右边一个次按钮「解除屏蔽」（44px 高）。
 * - 解除不弹确认（可逆，随时能再屏蔽；I 10-10）。成功：这一项消失，原位一句「已解除屏蔽」，读屏由这一节常驻的播报区礼貌地念（出字前就在），
 *   停 1.2 秒后收起；失败：这一项不动，下面一句「这次没解除，稍后再试。」。
 * - 空：「你还没有屏蔽过谁。」
 * 服务在查询函数里才取：读不到只落到这一节自己的出错样子，设置页别的部分照常。
 */
import { useId, useLayoutEffect, useRef, useState } from "react";
import { useInfiniteQuery, useQueryClient, type InfiniteData, type QueryClient } from "@tanstack/react-query";
import type { BlockedTarget, BlockedTargetPage, SessionState } from "@/shared/contracts";
import { isApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { useServices, useSocialLeaseReader } from "@/shared/services/registry";
import { useCurrentHousehold } from "@/shared/session/householdContext";
import { serverNow } from "@/shared/time/clock";
import { Button, Card, LoadingState } from "@/shared/ui";

/** 只按账号分：我屏蔽的不分是哪只宠物。 */
export const blockedTargetsKey = (userId: string) => ["social", "blocked", userId] as const;

/** 「已解除屏蔽」停多久再收起（I 10-10：1.2 秒）。 */
export const UNBLOCKED_HOLD_MS = 1200;
export const UNBLOCKED = "已解除屏蔽";

type Book = InfiniteData<BlockedTargetPage, string | null>;

/** 「10 月 9 日屏蔽」：按本机时区；不是今年的前面带年份；读不出时间返回 null（只显示名字）。 */
export function blockedOnText(iso: string, now: Date = new Date(serverNow())): string | null {
  const at = new Date(iso);
  if (!Number.isFinite(at.getTime())) return null;
  const day = `${at.getMonth() + 1} 月 ${at.getDate()} 日屏蔽`;
  return at.getFullYear() === now.getFullYear() ? day : `${at.getFullYear()} 年 ${day}`;
}

/** 翻下一页时后端说游标看不懂（422，details.reason = bad_cursor）：回第一页重新看。 */
export function isBadCursor(error: unknown): boolean {
  return isApiError(error) && error.status === 422 && error.details?.reason === "bad_cursor";
}

/** 从缓存里拿掉解除了的那一项（每一页都看，按 target_ref）。 */
function without(book: Book | undefined, ref: string): Book | undefined {
  if (!book) return book;
  return { ...book, pages: book.pages.map((page) => ({ ...page, items: page.items.filter((item) => item.target_ref !== ref) })) };
}

// Local account scope: the exported user key stays a compatibility prefix.
// One application identity/service provider per QueryClient, as in the app.
type BlockOwner = { userId: string; mode: string; serviceId: number; epoch: number; valid: boolean; reader: ReturnType<typeof useSocialLeaseReader>; key: readonly unknown[] };
const socialIds = new WeakMap<object, number>();
const blockOwners = new WeakMap<QueryClient, BlockOwner>();
let nextSocialId = 0;
let nextBlockEpoch = 0;
function ownerFor(client: QueryClient, social: object, userId: string, mode: string, reader: ReturnType<typeof useSocialLeaseReader>): BlockOwner {
  let serviceId = socialIds.get(social);
  if (serviceId === undefined) { serviceId = ++nextSocialId; socialIds.set(social, serviceId); }
  const old = blockOwners.get(client);
  if (old?.valid && old.userId === userId && old.mode === mode && old.serviceId === serviceId && old.reader === reader) return old;
  const epoch = ++nextBlockEpoch;
  const owner = { userId, mode, serviceId, epoch, valid: true, reader, key: [...blockedTargetsKey(userId), "reader", mode, serviceId, epoch] };
  return owner;
}
function accountCurrent(client: QueryClient, owner: BlockOwner): boolean {
  if (!owner.valid || blockOwners.get(client) !== owner) return false;
  if (owner.mode !== "live") return true;
  const session = client.getQueryData<SessionState>(["identity", "session"]);
  if (session !== undefined && (!session.authenticated || session.user?.user_id !== owner.userId)) {
    owner.valid = false;
    return false;
  }
  return true;
}
class BlockScopeEnded extends Error {
  constructor() { super("Blocked list context ended"); }
}

export function BlockedSection() {
  const services = useServices();
  const queryClient = useQueryClient();
  const { userId } = useCurrentHousehold();
  const reader = useSocialLeaseReader();
  const [owner, setOwner] = useState(() => ownerFor(queryClient, services.social, userId ?? "-", env.dataMode, reader));
  useLayoutEffect(() => {
    const previous = blockOwners.get(queryClient);
    if (previous && previous !== owner) previous.valid = false;
    blockOwners.set(queryClient, owner);
  }, [owner, queryClient]);
  const serviceId = socialIds.get(services.social);
  if (!owner.valid || owner.userId !== (userId ?? "-") || owner.mode !== env.dataMode || owner.serviceId !== serviceId || owner.reader !== reader) {
    setOwner(ownerFor(queryClient, services.social, userId ?? "-", env.dataMode, reader));
    return null;
  }
  const session = queryClient.getQueryData<SessionState>(["identity", "session"]);
  if (env.dataMode === "live" && (!userId?.trim() || (session !== undefined && (!session.authenticated || session.user?.user_id !== userId)))) return null;
  return <OwnedBlockedSection key={owner.epoch} owner={owner} />;
}

function OwnedBlockedSection({ owner }: { owner: BlockOwner }) {
  const services = useServices();
  const queryClient = useQueryClient();
  const key = owner.key;
  const mount = useRef({ active: false, generation: 0 });
  const timers = useRef(0);
  const stopWatching = useRef<(() => void) | null>(null);
  useLayoutEffect(() => {
    const lifetime = mount.current;
    lifetime.active = true;
    lifetime.generation++;
    // Keep observing an explicit session transition only while a completed
    // unblock's timer survives a pure unmount. A -> logout -> A stays expired.
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.query.queryKey[0] !== "identity" || event.query.queryKey[1] !== "session") return;
      if (event.type === "removed") owner.valid = false;
      else accountCurrent(queryClient, owner);
    });
    stopWatching.current?.();
    stopWatching.current = unsubscribe;
    return () => {
      lifetime.active = false;
      lifetime.generation++;
      if (timers.current === 0) { unsubscribe(); stopWatching.current = null; }
    };
  }, [owner, queryClient]);
  const owns = (generation = mount.current.generation) => mount.current.active && mount.current.generation === generation && accountCurrent(queryClient, owner);
  const list = useInfiniteQuery({
    queryKey: key,
    queryFn: async ({ pageParam, signal }) => {
      const generation = mount.current.generation;
      const lease = owner.reader();
      const current = () => owns(generation) && lease.isCurrent();
      if (!current()) throw new BlockScopeEnded();
      try {
        const result = await services.social.blockedTargets(pageParam, signal);
        if (!current()) throw new BlockScopeEnded();
        return result;
      } catch (error) {
        // Do this before shared QueryCache's auth callback can see an old401.
        if (!current()) throw new BlockScopeEnded();
        throw error;
      }
    },
    retry: (count, error) => {
      if (error instanceof BlockScopeEnded || !owns()) return false;
      const retry = queryClient.getDefaultOptions().queries?.retry;
      return typeof retry === "function" ? retry(count, error) : retry === true || count < (typeof retry === "number" ? retry : retry === false ? 0 : 3);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last: BlockedTargetPage) => last.next_cursor ?? undefined,
    enabled: env.dataMode !== "live" || Boolean(owner.userId.trim()),
  });
  const pendingRef = useRef<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [gone, setGone] = useState<string[]>([]);
  const [live, setLive] = useState("");

  const unblock = async (item: BlockedTarget) => {
    const lease = owner.reader();
    if (pendingRef.current || !owns() || !lease.isCurrent()) return;
    const generation = mount.current.generation;
    const current = () => owns(generation) && lease.isCurrent();
    pendingRef.current = item.target_ref;
    setPending(item.target_ref);
    setFailed(null);
    try {
      await services.social.unblock(item.target_ref);
    } catch {
      if (!current()) return;
      pendingRef.current = null;
      setPending(null);
      setFailed(item.target_ref);
      return;
    }
    if (!current()) return;
    pendingRef.current = null;
    setPending(null);
    setGone((refs) => [...refs, item.target_ref]);
    setLive(UNBLOCKED);
    // 朋友圈里那位作者的动态之后会重新出现：只标记过期、下次打开时重读；这一节自己不现在重读（重读会让原位那一句提前没了）。
    void queryClient.invalidateQueries({ queryKey: ["social"], refetchType: "none" });
    // 停 1.2 秒再收起。离开这一页也照样从缓存里拿掉（定时器不随这一节卸载取消），回来不会再看到解除了的那一项。
    timers.current++;
    window.setTimeout(() => {
      // Cache rights can survive a pure unmount; UI rights cannot. A changed
      // account/service/mode/session permanently revokes both old rights.
      if (accountCurrent(queryClient, owner) && lease.isCurrent()) {
        queryClient.setQueryData<Book>(key, (book) => without(book, item.target_ref));
        if (owns(generation)) {
          setGone((refs) => refs.filter((ref) => ref !== item.target_ref));
          setLive((said) => (said === UNBLOCKED ? "" : said));
        }
      }
      timers.current--;
      if (!mount.current.active && timers.current === 0) { stopWatching.current?.(); stopWatching.current = null; }
    }, UNBLOCKED_HOLD_MS);
  };

  const more = async () => {
    const generation = mount.current.generation;
    const lease = owner.reader();
    if (!owns(generation) || !lease.isCurrent()) return;
    const result = await list.fetchNextPage();
    if (owns(generation) && lease.isCurrent() && result.isError && isBadCursor(result.error)) await queryClient.resetQueries({ queryKey: key, exact: true });
  };
  const retryList = () => { if (owns() && owner.reader().isCurrent()) void list.refetch(); };

  const items = list.data ? list.data.pages.flatMap((page) => page.items) : [];
  return (
    <Card className="ps-settings-blocked">
      <div className="ps-section-title">我屏蔽的</div>
      <p className="visually-hidden" aria-live="polite" aria-atomic="true" data-testid="blocked-live">
        {live}
      </p>
      {list.data ? (
        items.length === 0 && !list.hasNextPage ? (
          <p className="ps-settings-blocked__empty">你还没有屏蔽过谁。</p>
        ) : (
          <>
            <ul className="ps-settings-blocked__list" aria-label="我屏蔽的">
              {items.map((item) =>
                gone.includes(item.target_ref) ? (
                  // 读屏由上面常驻的播报区念过了，这一行只给眼睛看
                  <li key={item.target_ref} className="ps-settings-blocked__done" aria-hidden="true">
                    {UNBLOCKED}
                  </li>
                ) : (
                  <BlockedRow key={item.target_ref} item={item} busy={pending === item.target_ref} locked={pending !== null && pending !== item.target_ref} failed={failed === item.target_ref} onUnblock={() => void unblock(item)} />
                ),
              )}
            </ul>
            {list.hasNextPage ? (
              <div className="ps-settings-blocked__more">
                {list.isFetchNextPageError && !isBadCursor(list.error) ? (
                  <p className="ps-muted" role="status">
                    更早的没翻到，再试一次。
                  </p>
                ) : null}
                <Button variant="ghost" loading={list.isFetchingNextPage} onClick={() => void more()}>
                  再看更早的
                </Button>
              </div>
            ) : null}
          </>
        )
      ) : list.isError ? (
        <div className="ps-settings-blocked__more">
          <p className="ps-muted" role="status">
            这次没读到，稍后再试。
          </p>
          <Button variant="ghost" loading={list.isFetching} onClick={retryList}>
            再试一次
          </Button>
        </div>
      ) : (
        <LoadingState lines={1} />
      )}
    </Card>
  );
}

function BlockedRow({ item, busy, locked, failed, onUnblock }: { item: BlockedTarget; busy: boolean; locked: boolean; failed: boolean; onUnblock: () => void }) {
  const nameId = useId();
  const when = blockedOnText(item.created_at);
  return (
    <li className="ps-settings-blocked__item">
      <span className="ps-settings-blocked__who">
        <strong id={nameId}>{item.display_name}</strong>
        {when ? <small>{when}</small> : null}
      </span>
      <Button variant="secondary" loading={busy} disabled={locked} aria-describedby={nameId} onClick={onUnblock}>
        解除屏蔽
      </Button>
      {failed ? (
        <p className="ps-form-error ps-settings-blocked__error" role="alert">
          这次没解除，稍后再试。
        </p>
      ) : null}
    </li>
  );
}
